// Opening hours (SPEC section 5). The day-keyed shape is the legacy one, so
// existing sellers' hours keep working: { Monday: { open, from, to, note } }.
// Close earlier than open means overnight; equal means 24 hours. Status is
// computed in the shop's time zone (nothing is tied to one town).

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export type DayName = (typeof DAYS)[number];
export type DaySchedule = { open: boolean; from: string; to: string; note?: string };
export type WeekHours = Partial<Record<DayName, DaySchedule>>;
export type ShopHours = {
  week: WeekHours | null;
  byAppointment?: boolean;
  closedMessage?: string;
  away?: { until: string; message?: string } | null;
  timeZone?: string;
};

export type ShopStatus =
  | { state: 'open'; label: 'Open now'; detail: string }
  | { state: 'closing_soon'; label: 'Closing soon'; detail: string }
  | { state: 'closed'; label: 'Closed'; detail: string; message?: string }
  | { state: 'away'; label: 'Temporarily closed'; detail: string; message?: string }
  | { state: 'appointment'; label: 'By appointment'; detail: string }
  | { state: 'unknown'; label: ''; detail: '' };

export const DEFAULT_TIME_ZONE = 'Africa/Accra';
const CLOSING_SOON_MINUTES = 60;
const TIME = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function dayName(index: number): DayName {
  return DAYS[((index % 7) + 7) % 7] as DayName;
}

export function minutes(value: unknown): number | null {
  const match = TIME.exec(String(value ?? ''));
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

// "08:00" → "8:00 AM" (SPEC: 12-hour times).
export function formatTime(value: string): string {
  const total = minutes(value);
  if (total === null) return value;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${hours % 12 || 12}:${String(mins).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
}

function localClock(now: Date, timeZone: string): { day: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  const weekday = parts.find(part => part.type === 'weekday')?.value ?? 'Sun';
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);
  const hour = Number(parts.find(part => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find(part => part.type === 'minute')?.value ?? 0);
  return { day: day < 0 ? 0 : day, minute: hour * 60 + minute };
}

function enabled(schedule: DaySchedule | undefined): schedule is DaySchedule {
  return Boolean(schedule?.open) && minutes(schedule?.from) !== null && minutes(schedule?.to) !== null;
}

// Minutes until closing if open at `minute` under this day's schedule (or the
// previous day's overnight hours), else null.
function minutesUntilClose(week: WeekHours, day: number, minute: number): number | null {
  const today = week[dayName(day)];
  if (enabled(today)) {
    const open = minutes(today.from)!;
    const close = minutes(today.to)!;
    if (open === close) return Infinity;
    if (close > open && minute >= open && minute < close) return close - minute;
    if (close < open && minute >= open) return 24 * 60 - minute + close;
  }
  const yesterday = week[dayName(day + 6)];
  if (enabled(yesterday)) {
    const open = minutes(yesterday.from)!;
    const close = minutes(yesterday.to)!;
    if (close < open && minute < close) return close - minute;
  }
  return null;
}

function nextOpening(week: WeekHours, day: number, minute: number): string {
  const today = week[dayName(day)];
  if (enabled(today) && minute < minutes(today.from)!) return `Opens today at ${formatTime(today.from)}`;
  for (let offset = 1; offset <= 7; offset += 1) {
    const index = (day + offset) % 7;
    const schedule = week[dayName(index)];
    if (!enabled(schedule)) continue;
    return `Opens ${offset === 1 ? 'tomorrow' : dayName(index)} at ${formatTime(schedule.from)}`;
  }
  return 'No opening hours set';
}

export function shopStatus(hours: ShopHours | null | undefined, now: Date = new Date()): ShopStatus {
  if (!hours) return { state: 'unknown', label: '', detail: '' };
  const timeZone = hours.timeZone || DEFAULT_TIME_ZONE;
  const awayUntil = Date.parse(String(hours.away?.until || ''));
  if (Number.isFinite(awayUntil) && awayUntil > now.getTime()) {
    const until = new Intl.DateTimeFormat('en-GB', { timeZone, day: 'numeric', month: 'long' }).format(new Date(awayUntil));
    return { state: 'away', label: 'Temporarily closed', detail: `Back on ${until}`, ...(hours.away?.message ? { message: hours.away.message } : {}) };
  }
  if (hours.byAppointment) return { state: 'appointment', label: 'By appointment', detail: 'Message the shop to arrange a time' };
  const week = hours.week;
  if (!week || !DAYS.some(day => enabled(week[day]))) return { state: 'unknown', label: '', detail: '' };
  const { day, minute } = localClock(now, timeZone);
  const left = minutesUntilClose(week, day, minute);
  if (left === Infinity) return { state: 'open', label: 'Open now', detail: 'Open 24 hours' };
  if (left !== null) {
    const closeAt = formatTime(`${String(Math.floor(((minute + left) % 1440) / 60)).padStart(2, '0')}:${String((minute + left) % 60).padStart(2, '0')}`);
    return left <= CLOSING_SOON_MINUTES
      ? { state: 'closing_soon', label: 'Closing soon', detail: `Closes at ${closeAt}` }
      : { state: 'open', label: 'Open now', detail: `Closes at ${closeAt}` };
  }
  return { state: 'closed', label: 'Closed', detail: nextOpening(week, day, minute), ...(hours.closedMessage ? { message: hours.closedMessage } : {}) };
}

// Only orders while temporarily closed are refused (SPEC section 5).
export function acceptsOrders(hours: ShopHours | null | undefined, now: Date = new Date()): boolean {
  return shopStatus(hours, now).state !== 'away';
}

// Open for orders at this moment: shops without hours, or by appointment,
// count as always open; temporarily closed counts as shut.
export function isOpenAt(hours: ShopHours | null | undefined, at: Date): boolean {
  const state = shopStatus(hours, at).state;
  return state !== 'closed' && state !== 'away';
}

const STEP_MS = 5 * 60_000;
const LOOK_AHEAD_MS = 8 * 24 * 60 * 60_000;

// When the shop next opens (5-minute steps), `from` itself when open now,
// or null when it does not open in the next 8 days.
export function nextOpenAt(hours: ShopHours | null | undefined, from: Date): Date | null {
  for (let at = from.getTime(); at <= from.getTime() + LOOK_AHEAD_MS; at += STEP_MS) {
    if (isOpenAt(hours, new Date(at))) return new Date(at);
  }
  return null;
}

// The moment a shop has been open for `minutes` counted from `from`: closed
// hours do not count, so a night order is not timed out before morning.
// Hours that never open fall back to plain clock time.
export function openWindowEnd(hours: ShopHours | null | undefined, from: Date, minutes: number): Date {
  const needed = Math.ceil((minutes * 60_000) / STEP_MS);
  let counted = 0;
  for (let at = from.getTime(); at <= from.getTime() + LOOK_AHEAD_MS; at += STEP_MS) {
    if (isOpenAt(hours, new Date(at))) counted += 1;
    if (counted >= needed) return new Date(at + STEP_MS);
  }
  return new Date(from.getTime() + minutes * 60_000);
}

export const MAX_SCHEDULE_DAYS = 7;
const MIN_SCHEDULE_LEAD_MS = 15 * 60_000;

// A time a buyer asks for: at least 15 minutes ahead, within a week, while
// the shop is open.
export function checkScheduledTime(hours: ShopHours | null | undefined, at: Date, now: Date): { ok: true } | { ok: false; message: string } {
  if (!Number.isFinite(at.getTime())) return { ok: false, message: 'Choose a time for your order.' };
  if (at.getTime() < now.getTime() + MIN_SCHEDULE_LEAD_MS) return { ok: false, message: 'Choose a time at least 15 minutes from now.' };
  if (at.getTime() > now.getTime() + MAX_SCHEDULE_DAYS * 24 * 60 * 60_000) return { ok: false, message: `Choose a time within ${MAX_SCHEDULE_DAYS} days.` };
  if (!isOpenAt(hours, at)) return { ok: false, message: 'The shop is closed then. Choose a time in its opening hours.' };
  return { ok: true };
}

export type HoursParse = { ok: true; value: ShopHours } | { ok: false; message: string };

export function parseShopHours(raw: unknown): HoursParse {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const weekInput = (input.week && typeof input.week === 'object' ? input.week : {}) as Record<string, unknown>;
  const week: WeekHours = {};
  for (const day of DAYS) {
    const item = weekInput[day] as Record<string, unknown> | undefined;
    if (!item) continue;
    const open = item.open === true;
    const from = String(item.from || '08:00');
    const to = String(item.to || '18:00');
    if (minutes(from) === null || minutes(to) === null) return { ok: false, message: `Check the times for ${day}.` };
    week[day] = { open, from, to, note: String(item.note || '').trim().slice(0, 80) };
  }
  const away = input.away as Record<string, unknown> | null | undefined;
  let awayValue: ShopHours['away'] = null;
  if (away && away.until) {
    const until = Date.parse(String(away.until));
    if (!Number.isFinite(until)) return { ok: false, message: 'Choose when the shop reopens.' };
    awayValue = { until: new Date(until).toISOString(), message: String(away.message || '').trim().slice(0, 200) };
  }
  const timeZone = String(input.timeZone || DEFAULT_TIME_ZONE);
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone });
  } catch {
    return { ok: false, message: 'The time zone is invalid.' };
  }
  return {
    ok: true,
    value: {
      week, byAppointment: input.byAppointment === true, closedMessage: String(input.closedMessage || '').trim().slice(0, 200),
      away: awayValue, timeZone,
    },
  };
}

export const HOURS_PRESETS: Record<string, WeekHours> = {
  weekdays: Object.fromEntries(DAYS.map(day => [day, { open: day !== 'Saturday' && day !== 'Sunday', from: '08:00', to: '17:00' }])),
  everyday: Object.fromEntries(DAYS.map(day => [day, { open: true, from: '08:00', to: '20:00' }])),
  evenings: Object.fromEntries(DAYS.map(day => [day, { open: true, from: '17:00', to: '23:00' }])),
};
